import React, { useEffect, useState } from "react";
import { Typography, Paper, Box, Button, TextField, Card, CardContent, Grid, Divider, FormControl, Select, MenuItem, InputAdornment, Slider, Alert, AlertTitle } from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as styles from "../BackofficeRequest/Styles.js";
import Colors from "../../assets/Variables/ColorsVariables.js";
import { Spaces } from "../../assets/Variables/SpaceVariables.js";
import * as TextVariables from "../../assets/Variables/TextVariables.js";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SpeedIcon from "@mui/icons-material/Speed";
import PlatformIcon from "@mui/icons-material/Devices";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import PercentIcon from '@mui/icons-material/Percent';
import CommentIcon from '@mui/icons-material/Comment';
import { GetServersdata, RemoveServersData, SetServersdata } from "../../Tools/LocalStorage.js";
import ServersRequest from "../../Endpoints/ServersRequest.js";
import CopierPerformanceFeeRequest from "../../Endpoints/CopierPerformanceFeeRequest.js";
import NotificationModal from "../../components/Notifications/NotificationModal.js";

function CopierPerformanceFee() {
  const navigate = useNavigate();
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [signalProviderLogin, setSignalProviderLogin] = useState("");
  const [performanceFee, setPerformanceFee] = useState(0);
  const [additionalComments, setAdditionalComments] = useState("");
  const [servers, setServers] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });

  useEffect(() => {
    const serversData = GetServersdata();
    if(serversData.length > 0){
      const orderedServers = orderServers(serversData);
      const filteredServers = filterCopierServers(orderedServers);
      
      setServers(filteredServers)
      setSelectedPlatform(filteredServers[0].value);
    }
    else{
      RequestServersData();
    }
  }, []);

  const RequestServersData = async () => {
    try{
      const response = await ServersRequest();
      if (response.data.data != null) {
        let actualRows = response.data.data.servers
        .map((server, index) => {
          let row = {
              label: server,
              value: server
          }
          return row;
        });
        const orderedServers = orderServers(actualRows);
        const filteredServers = filterCopierServers(orderedServers);
        
        setServers(filteredServers)
        setSelectedPlatform(filteredServers[0].value);
        SetServersdata(orderedServers)
      }
      else{
        setServers([])
        RemoveServersData();
      }
    }
    catch(error){
      setServers([])
      RemoveServersData();
      console.log(error);
    }
  }  
  
  const orderServers = (servers) => {
    const platformOrder = ["MT4", "MT5", "CTRADER"];
    const typeOrder = ["LIVE", "DEMO", "TEST"];

    return [...servers].sort((a, b) => {
      const A = a.label.toUpperCase();
      const B = b.label.toUpperCase();

      // LTD first
      const aLTD = A.includes("LTD") ? 0 : 1;
      const bLTD = B.includes("LTD") ? 0 : 1;
      if (aLTD !== bLTD) return aLTD - bLTD;

      // Platform: MT4 -> MT5 -> cTrader
      const aPlatform = platformOrder.findIndex(p => A.includes(p));
      const bPlatform = platformOrder.findIndex(p => B.includes(p));

      if (aPlatform !== bPlatform) return aPlatform - bPlatform;

      // LIVE -> DEMO -> TEST
      const aType = typeOrder.findIndex(t => A.includes(t));
      const bType = typeOrder.findIndex(t => B.includes(t));

      if (aType !== bType) return aType - bType;

      // Number at the end
      const aNum = parseInt(A.match(/\d+$/)?.[0] || "0", 10);
      const bNum = parseInt(B.match(/\d+$/)?.[0] || "0", 10);

      return aNum - bNum;
    });
  };

  const filterCopierServers = (servers) => {
    const validNames = new Set([
      "MT4 LTD Live 1",
      "MT4 LTD Live 2",
      "MT5 LTD Live 1",
      "MT4 LTD Demo",
      "MT5 LTD Demo"
    ]);

    const filteredServers = orderServers(servers).filter(server => validNames.has(server.label));
    return filteredServers;
  }

  const handleClickToBackPage = () => navigate("/requests");

  const handleCloseNotification = () => setShowNotification(false);

  const handleSliderChange = (event, newValue) => {
    setPerformanceFee(newValue);
  };

  const handleInputChange = (event) => {
    let value = parseInt(event.target.value) || 0;
    if (value < 0) value = 0;
    if (value > 50) value = 50;
    setPerformanceFee(value);
  };

  const handleSubmit = async () => {
    try{
      const response = await CopierPerformanceFeeRequest({
        platform: selectedPlatform,
        signal: signalProviderLogin,
        performance_fee: performanceFee,
        comment: additionalComments,
      });

      if (response.status === 200) {
        setModalProps({
          iconType: "success",
          title: "Request sent",
          message: response.data.message || "Your request has been successfully submitted.",
          onAcceptLabel: "Continue",
          onRefuseLabel: undefined,
          onAccept: () => navigate("/requests"),
          onRefuse: undefined,
        });
        setShowNotification(true);
      }
      else{
        setModalProps({
          iconType: "error",
          title: "Request failed",
          message: response.data.message || "Failed to submit request.",
          onAcceptLabel: "Try Again",
          onRefuseLabel: undefined,
          onAccept: () => setShowNotification(false),
          onRefuse: undefined,
        });
        setShowNotification(true);
        console.log("Error submitting request:", response);
      }
    }
    catch(error){
      if (error?.response) {
        if (error.response.status === 400 || error.response.status === 404) {
          setModalProps({
            iconType: "error",
            title: "Problem with request",
            message: error.response.data?.message,
            onAcceptLabel: "Try again",
            onAccept: () => setShowNotification(false),
          });
        } else {
          setModalProps({
            iconType: "error",
            title: "Problem with service",
            message: "The following error has been caused: " + error.response.data?.message + ". please contact an administrator",
            onAcceptLabel: "Try again",
            onAccept: () => setShowNotification(false),
          });
        }
      } else {
        setModalProps({
          iconType: "error",
          title: "Problem with service",
          message: "Please contact an administrator - " + error?.message,
          onAcceptLabel: "Accept",
          onAccept: () => setShowNotification(false),
        });
      }
      
      setShowNotification(true);
    }
  }  

  const isFormValid = () => {
    return selectedPlatform && signalProviderLogin && performanceFee >= 0 && performanceFee <= 50;
  };

  return (
    <Box sx={styles.mainBox}>
      <NotificationModal
        open={showNotification}
        onClose={handleCloseNotification}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />
      <Box sx={styles.secondBox}>
        <Box sx={styles.backButtonContainer}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              onClick={handleClickToBackPage}
              startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
              sx={{ textTransform: "none", color: "black" }}
            >
              Go back
            </Button>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            ...(styles.mainPaper || {}),
            borderRadius: "12px",
            padding: { xs: 2, sm: 3, md: Spaces.Size_5 },
            width: "100%",
            maxWidth: "1300px",
            mx: "auto",
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              sx={{ 
                fontFamily: TextVariables.Body_3_Bold, 
                fontSize: { xs: "1.2rem", md: "1.5rem" } 
              }}
            >
              Copier Performance Fee Request
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={1}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  border: `1px solid ${Colors.Gray_200}`,
                  backgroundColor: "#f5f8fb",
                  overflow: "hidden",
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 60, md: 74 },
                      height: { xs: 60, md: 74 },
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <RequestPageIcon
                      sx={{
                        fontSize: { xs: 32, md: 42 },
                        color: Colors.Primary_600,
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: TextVariables.Body_2_Bold,
                      fontSize: { xs: "22px", md: "28px" },
                      color: Colors.Gray_900,
                      mb: 2,
                    }}
                  >
                    Copier Fee
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: TextVariables.Body_4,
                      color: Colors.Gray_600,
                      maxWidth: "100%",
                      lineHeight: 1.6,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    Create a personalized link with the percentage commission to be applied to copied trades
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2, borderRadius: "12px", overflow: "hidden", boxShadow: 1 }}>
                <Box sx={{ bgcolor: Colors.Primary_600, px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
                    Request Overview
                  </Typography>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PlatformIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Platform:</strong> {selectedPlatform ?? "Not selected"}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ConnectWithoutContactIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Signal Provider Login:</strong> {signalProviderLogin || "Not set"}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PercentIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Performance Fee:</strong> {performanceFee > 0 ? `${performanceFee}%` : "Not set"}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SpeedIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Maximum Allowed Fee:</strong> 50%
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ShowChartIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Applies to:</strong> Future profits
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CommentIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                      <Typography variant="body2">
                        <strong>Additional Comments:</strong> {additionalComments || "None"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Columna derecha */}
            <Grid item xs={12} md={8}>
              {/* Trading Platform */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Trading Platform <Typography component="span" color="error">*</Typography>
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    displayEmpty
                    sx={{ bgcolor: "white" }}
                  >
                    {servers.map(platform => ( <MenuItem key={platform.value} value={platform.value}>{platform.label}</MenuItem> ))}
                  </Select>
                </FormControl>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Signal Provider Login <Typography component="span" color="error">*</Typography>
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter signal provider login (numeric)"
                    value={signalProviderLogin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setSignalProviderLogin(value);
                    }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Performance Fee (%)
                </Typography>
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 2, sm: 2 }
                  }}
                >
                  <TextField
                    size="small"
                    value={performanceFee}
                    onChange={handleInputChange}
                    placeholder="Enter fee percentage"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    sx={{ width: { xs: "100%", sm: 150 }, bgcolor: "white" }}
                  />
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      flex: 1,
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                      0%
                    </Typography>
                    <Slider
                      value={performanceFee}
                      onChange={handleSliderChange}
                      min={0}
                      max={50}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                      sx={{
                        color: Colors.Primary_600,
                        flex: 1,
                        "& .MuiSlider-thumb": {
                          height: 20,
                          width: 20,
                          backgroundColor: Colors.Primary_600,
                        },
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                      50%
                    </Typography>
                  </Box>
                </Box>

                <Alert
                  severity="info"
                  sx={{
                    mt: 3,
                    borderRadius: "8px",
                    backgroundColor: Colors.Info_50,
                    borderLeft: `4px solid ${Colors.Info_600}`,
                  }}
                >
                  <AlertTitle sx={{ fontWeight: "bold" }}>Performance Fee</AlertTitle>
                  Performance Fee is the percentage deducted from followers' profits.
                </Alert>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Additional Comments (Optional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter any additional comments or special requests here..."
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value.slice(0, 300))}
                  helperText={`${additionalComments.length} / 300 characters`}
                  sx={{ bgcolor: "white" }}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" }, mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  fullWidth={false}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                    padding: "10px 32px",
                    fontSize: "14px",
                    width: { xs: "100%", md: "auto" },
                    backgroundColor: isFormValid() ? Colors.Primary_600 : "#E5E7EB",
                    color: isFormValid() ? "#fff" : "#6B7280",
                    "&:hover": {
                      backgroundColor: isFormValid() ? Colors.Primary_700 : "#E5E7EB",
                    },
                  }}
                >
                  Submit Request
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}

export default CopierPerformanceFee;